import React, { useState } from "react";
import { ButtonWrapper } from "../CancelModal/Styled";
import CustomButton from "../TitleRow/CustomButton";
import LoadingButton from "../LoadingButton";

export interface ValidationRow {
    rowNumber: number;
    data: any;
    isValid: boolean;
    errors: Array<{
        row: number;
        field?: string;
        error: string;
        data?: any;
        isWarning?: boolean; // If true, this is a warning and shouldn't block navigation
    }>;
}

export interface SummaryStat {
    value: number;
    label: string;
    backgroundColor?: string;
    borderColor?: string;
    textColor?: string;
}

export interface ImportStepsProps {
    // Step indicator
    currentStep: "file" | "validation" | "confirmation";
    
    // Step 1 props
    selectedFile: File | null;
    importMode: "flush" | "update" | null;
    onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onImportModeChange: (mode: "flush" | "update") => void;
    onNext: () => void;
    fileInputRef: React.RefObject<HTMLInputElement>;
    isUploading: boolean;
    hasValidationErrors: boolean;
    
    // Step 2 props
    validatedRows: ValidationRow[];
    onBack: () => void;
    
    // Step 3 props
    onImport: () => void;
    
    // Config
    title: string;
    importModeLabels?: {
        update: { title: string; description: string };
        flush: { title: string; description: string; warning: string };
    };
    fileFormatInstructions?: React.ReactNode;
    previewColumns?: Array<{ key: string; label: string; isNested?: boolean }>;
    summaryStats?: SummaryStat[]; // Custom summary stats, if not provided uses default calculation
    templateUrl?: string; // URL to download template file
}

export const ImportSteps = ({
    currentStep,
    selectedFile,
    importMode,
    onFileChange,
    onImportModeChange,
    onNext,
    fileInputRef,
    isUploading,
    hasValidationErrors,
    validatedRows,
    onBack,
    onImport,
    title,
    importModeLabels = {
        update: {
            title: "Cập nhật và thêm mới",
            description: "Dữ liệu mới sẽ được thêm vào. Dữ liệu trùng mã sẽ được cập nhật với dữ liệu từ file.",
        },
        flush: {
            title: "Xóa tất cả và nhập mới",
            description: "CẢNH BÁO: Tất cả dữ liệu hiện có sẽ bị xóa vĩnh viễn và thay thế bằng dữ liệu từ file. Hành động này không thể hoàn tác.",
            warning: "CẢNH BÁO",
        },
    },
    fileFormatInstructions,
    previewColumns = [
        { key: "name", label: "Tên" },
        { key: "code", label: "Mã" },
    ],
    summaryStats,
    templateUrl,
}: ImportStepsProps) => {
    const [copyStatus, setCopyStatus] = useState<"idle" | "success" | "error">("idle");
    const validRows = validatedRows.filter((r) => r.isValid);
    const invalidRows = validatedRows.filter((r) => !r.isValid);
    
    // Collect all errors and warnings from all rows
    const allErrors = validatedRows.flatMap((row) => {
        if (row.errors && Array.isArray(row.errors) && row.errors.length > 0) {
            return row.errors;
        }
        return [];
    });
    
    // Separate blocking errors from warnings
    const blockingErrors = allErrors.filter(e => !e.isWarning);
    const warnings = allErrors.filter(e => e.isWarning);
    
    // Calculate default summary stats if not provided
    const defaultSummaryStats: SummaryStat[] = summaryStats || [
        {
            value: validRows.length,
            label: "Dòng hợp lệ",
            backgroundColor: "#e8f5e9",
            borderColor: "#4caf50",
            textColor: "#2e7d32",
        },
        {
            value: invalidRows.length,
            label: "Lỗi",
            backgroundColor: invalidRows.length > 0 ? "#ffebee" : "#e8f5e9",
            borderColor: invalidRows.length > 0 ? "#f44336" : "#4caf50",
            textColor: invalidRows.length > 0 ? "#c62828" : "#2e7d32",
        },
        {
            value: validatedRows.length,
            label: "Tổng số dòng",
            backgroundColor: "#e3f2fd",
            borderColor: "#2196f3",
            textColor: "#1565c0",
        },
    ];
    
    const displaySummaryStats = summaryStats || defaultSummaryStats;

    // Step Indicator
    const StepIndicator = () => (
        <div style={{ marginBottom: "24px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
                <div
                    style={{
                        width: "32px",
                        height: "32px",
                        borderRadius: "50%",
                        backgroundColor: currentStep === "file" ? "#2196f3" : "#4caf50",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "bold",
                    }}
                >
                    1
                </div>
                <div style={{ flex: 1, height: "2px", backgroundColor: currentStep !== "file" ? "#4caf50" : "#e0e0e0" }} />
                <div
                    style={{
                        width: "32px",
                        height: "32px",
                        borderRadius: "50%",
                        backgroundColor:
                            currentStep === "validation"
                                ? "#2196f3"
                                : currentStep === "confirmation"
                                ? "#4caf50"
                                : "#e0e0e0",
                        color: currentStep === "confirmation" ? "white" : currentStep === "validation" ? "white" : "#666",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "bold",
                    }}
                >
                    2
                </div>
                <div style={{ flex: 1, height: "2px", backgroundColor: currentStep === "confirmation" ? "#4caf50" : "#e0e0e0" }} />
                <div
                    style={{
                        width: "32px",
                        height: "32px",
                        borderRadius: "50%",
                        backgroundColor: currentStep === "confirmation" ? "#2196f3" : "#e0e0e0",
                        color: currentStep === "confirmation" ? "white" : "#666",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "bold",
                    }}
                >
                    3
                </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "#666" }}>
                <span>Chọn file & Chế độ</span>
                <span>Xác thực & Xem trước</span>
                <span>Xác nhận</span>
            </div>
        </div>
    );

    // Step 1: File Selection
    const Step1FileSelection = () => (
        <>
            <h3 style={{ marginTop: 0, marginBottom: "20px", fontSize: "18px", fontWeight: 500 }}>
                Bước 1: Chọn file và chế độ nhập
            </h3>

            <div style={{ marginBottom: "20px" }}>
                <label
                    htmlFor="file-upload"
                    style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "14px",
                        fontWeight: 500,
                    }}
                >
                    Chọn file Excel (.xlsx, .xls) <span style={{ color: "#d32f2f" }}>*</span>
                </label>
                <input
                    ref={fileInputRef}
                    id="file-upload"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={onFileChange}
                    disabled={isUploading}
                    style={{
                        width: "100%",
                        padding: "8px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        fontSize: "14px",
                    }}
                />
                {selectedFile && (
                    <div
                        style={{
                            marginTop: "8px",
                            fontSize: "12px",
                            color: "#666",
                        }}
                    >
                        Đã chọn: {selectedFile.name}
                    </div>
                )}
            </div>

            <div style={{ marginBottom: "20px" }}>
                <label
                    style={{
                        display: "block",
                        marginBottom: "12px",
                        fontSize: "14px",
                        fontWeight: 500,
                    }}
                >
                    Chế độ nhập dữ liệu <span style={{ color: "#d32f2f" }}>*</span>
                </label>
                <div
                    style={{
                        padding: "12px",
                        backgroundColor: "#f9f9f9",
                        borderRadius: "4px",
                        border: "1px solid #e0e0e0",
                    }}
                >
                    <label
                        style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "8px",
                            fontSize: "14px",
                            cursor: "pointer",
                            marginBottom: "12px",
                            padding: "8px",
                            borderRadius: "4px",
                            backgroundColor: importMode === "update" ? "#e3f2fd" : "transparent",
                            border: importMode === "update" ? "1px solid #2196f3" : "1px solid transparent",
                        }}
                    >
                        <input
                            type="radio"
                            name="importMode"
                            value="update"
                            checked={importMode === "update"}
                            onChange={() => onImportModeChange("update")}
                            disabled={isUploading}
                            style={{ marginTop: "2px" }}
                        />
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 500, marginBottom: "4px" }}>{importModeLabels.update.title}</div>
                            <div style={{ fontSize: "12px", color: "#666" }}>{importModeLabels.update.description}</div>
                        </div>
                    </label>
                    <label
                        style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "8px",
                            fontSize: "14px",
                            cursor: "pointer",
                            padding: "8px",
                            borderRadius: "4px",
                            backgroundColor: importMode === "flush" ? "#ffebee" : "transparent",
                            border: importMode === "flush" ? "1px solid #f44336" : "1px solid transparent",
                        }}
                    >
                        <input
                            type="radio"
                            name="importMode"
                            value="flush"
                            checked={importMode === "flush"}
                            onChange={() => onImportModeChange("flush")}
                            disabled={isUploading}
                            style={{ marginTop: "2px" }}
                        />
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 500, marginBottom: "4px", color: "#d32f2f" }}>
                                {importModeLabels.flush.title}
                            </div>
                            <div style={{ fontSize: "12px", color: "#d32f2f" }}>
                                <strong>{importModeLabels.flush.warning}:</strong> {importModeLabels.flush.description}
                            </div>
                        </div>
                    </label>
                </div>
            </div>

            {templateUrl && (
                <div style={{ marginBottom: "12px" }}>
                    <a
                        href={templateUrl}
                        download
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            padding: "8px 12px",
                            backgroundColor: "#2196f3",
                            color: "white",
                            textDecoration: "none",
                            borderRadius: "4px",
                            fontSize: "13px",
                            fontWeight: 500,
                            cursor: "pointer",
                            border: "none",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#1976d2";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "#2196f3";
                        }}
                    >
                        <span>📥</span>
                        <span>Tải file mẫu</span>
                    </a>
                </div>
            )}

            {fileFormatInstructions && (
                <div
                    style={{
                        padding: "12px",
                        backgroundColor: "#f5f5f5",
                        borderRadius: "4px",
                        fontSize: "12px",
                        color: "#666",
                        marginBottom: "20px",
                    }}
                >
                    {fileFormatInstructions}
                </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "16px", marginTop: "24px" }}>
                <LoadingButton
                    variant="contained"
                    onClick={onNext}
                    disabled={!selectedFile || !importMode || isUploading || hasValidationErrors}
                    isLoading={false}
                    text="Tiếp theo"
                    style={{
                        paddingTop: 8,
                        paddingBottom: 8,
                        display: "flex",
                        justifyContent: "center",
                        minWidth: "120px",
                        borderRadius: "5px",
                        fontSize: "14px",
                    }}
                />
            </div>
        </>
    );

    // Step 2: Validation & Preview
    const Step2Validation = () => {
        return (
        <>
            <h3 style={{ marginTop: 0, marginBottom: "20px", fontSize: "18px", fontWeight: 500 }}>
                Bước 2: Xác thực và xem trước dữ liệu
            </h3>

            {/* Summary */}
            <div
                style={{
                    display: "flex",
                    gap: "12px",
                    marginBottom: "20px",
                    width: "100%",
                }}
            >
                {displaySummaryStats.map((stat, index) => (
                    <div
                        key={index}
                        style={{
                            flex: 1,
                            padding: "8px 12px",
                            backgroundColor: stat.backgroundColor || "#e8f5e9",
                            borderRadius: "4px",
                            border: `1px solid ${stat.borderColor || "#4caf50"}`,
                        }}
                    >
                        <div style={{ fontSize: "18px", fontWeight: "bold", color: stat.textColor || "#2e7d32" }}>
                            {stat.value}
                        </div>
                        <div style={{ fontSize: "11px", color: stat.textColor || "#2e7d32" }}>{stat.label}</div>
                    </div>
                ))}
            </div>

            {/* Always show error log if there are any errors or invalid rows */}
            {(allErrors.length > 0 || invalidRows.length > 0 || validatedRows.length === 0) && (
                <>
                    {(() => {
                        const debugPayload = {
                            total_errors: allErrors.length,
                            invalid_rows_count: invalidRows.length,
                            rows_with_errors_count: validatedRows.filter(r => r.errors && r.errors.length > 0).length,
                            total_validated_rows: validatedRows.length,
                            invalid_rows: invalidRows.map((row) => ({
                                row: row.rowNumber,
                                isValid: row.isValid,
                                errors: row.errors || [],
                            })),
                            rows_with_errors: validatedRows
                                .filter(r => r.errors && r.errors.length > 0)
                                .map((row) => ({
                                    row: row.rowNumber,
                                    isValid: row.isValid,
                                    errors: row.errors || [],
                                })),
                            all_errors: allErrors,
                        };
                        const debugText = JSON.stringify(debugPayload);

                        const handleCopyDebug = () => {
                            try {
                                if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                                    navigator.clipboard.writeText(debugText);
                                } else {
                                    // Fallback for older browsers
                                    const textarea = document.createElement("textarea");
                                    textarea.value = debugText;
                                    textarea.style.position = "fixed";
                                    textarea.style.left = "-9999px";
                                    document.body.appendChild(textarea);
                                    textarea.select();
                                    document.execCommand("copy");
                                    document.body.removeChild(textarea);
                                }
                                setCopyStatus("success");
                            } catch (e) {
                                // Silent failure; this is a debug-only helper
                                console.error("Failed to copy debug log", e);
                                setCopyStatus("error");
                            }
                        };

                        return (
                    <div
                        style={{
                            padding: "12px",
                            backgroundColor: blockingErrors.length > 0 ? "#fff3e0" : warnings.length > 0 ? "#fff9e6" : "#e3f2fd",
                            borderRadius: "4px",
                            border: blockingErrors.length > 0 ? "1px solid #ff9800" : warnings.length > 0 ? "1px solid #ffc107" : "1px solid #2196f3",
                            marginBottom: "20px",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: "8px",
                                fontWeight: 500,
                                color: blockingErrors.length > 0 ? "#e65100" : warnings.length > 0 ? "#f57c00" : "#1565c0",
                                marginBottom: "8px",
                            }}
                        >
                            <span>
                                {blockingErrors.length > 0 ? (
                                    <>⚠️ Có {blockingErrors.length} lỗi ({invalidRows.length} dòng không hợp lệ). Vui lòng kiểm tra và sửa các lỗi trước khi tiếp tục.</>
                                ) : warnings.length > 0 ? (
                                    <>ℹ️ Có {warnings.length} cảnh báo (không chặn việc tiếp tục). Vui lòng xem chi tiết bên dưới.</>
                                ) : validatedRows.length === 0 ? (
                                    <>ℹ️ Không có dữ liệu được xác thực. Vui lòng kiểm tra file Excel.</>
                                ) : (
                                    <>✓ Không có lỗi</>
                                )}
                            </span>
                            {(blockingErrors.length > 0 || warnings.length > 0) && (
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                    <button
                                        type="button"
                                        onClick={handleCopyDebug}
                                        style={{
                                            fontSize: "10px",
                                            padding: "4px 8px",
                                            borderRadius: "3px",
                                            border: "1px solid #e0e0e0",
                                            backgroundColor: "#ffffff",
                                            cursor: "pointer",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        Sao chép log lỗi
                                    </button>
                                    {copyStatus === "success" && (
                                        <span style={{ fontSize: "10px", color: "#2e7d32" }}>
                                            Sao chép thành công
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                        {(blockingErrors.length > 0 || warnings.length > 0) && (
                            <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                                {/* Show all rows with errors, not just invalid ones */}
                                {validatedRows.filter(r => r.errors && r.errors.length > 0).length > 0 ? (
                                    <>
                                        {validatedRows
                                            .filter(r => r.errors && r.errors.length > 0)
                                            .slice(0, 30)
                                            .map((row) => (
                                            <div key={row.rowNumber} style={{ marginBottom: "8px", fontSize: "12px", padding: "6px", backgroundColor: row.errors?.some(e => e.isWarning) ? "#fff9e6" : "#ffebee", borderRadius: "2px", border: row.errors?.some(e => e.isWarning) ? "1px solid #ffc107" : "1px solid #f44336" }}>
                                                <div style={{ fontWeight: 600, marginBottom: "4px" }}>
                                                    Dòng {row.rowNumber} {row.errors?.some(e => e.isWarning) ? "(Cảnh báo)" : "(Lỗi)"}:
                                                </div>
                                                {row.errors && Array.isArray(row.errors) && row.errors.length > 0 ? (
                                                    row.errors.map((e, idx) => (
                                                        <div key={idx} style={{ marginLeft: "12px", marginTop: "2px", fontSize: "11px" }}>
                                                            <span style={{ fontWeight: 500, color: e.isWarning ? "#f57c00" : "#d32f2f" }}>
                                                                {e.field ? `[${e.field}] ` : ""}
                                                            </span>
                                                            <span style={{ color: e.isWarning ? "#f57c00" : "#d32f2f" }}>{e.error || "Lỗi không xác định"}</span>
                                                            {e.data && (
                                                                <span style={{ color: "#666", fontSize: "10px", marginLeft: "4px" }}>
                                                                    (Giá trị: {String(e.data).substring(0, 50)})
                                                                </span>
                                                            )}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <span style={{ color: "#666" }}>Lỗi không xác định</span>
                                                )}
                                            </div>
                                        ))}
                                        {validatedRows.filter(r => r.errors && r.errors.length > 0).length > 30 && (
                                            <div style={{ fontSize: "12px", color: "#666", marginTop: "8px", fontWeight: 500 }}>
                                                ... và {validatedRows.filter(r => r.errors && r.errors.length > 0).length - 30} dòng có lỗi khác
                                            </div>
                                        )}
                                    </>
                                ) : allErrors.length > 0 ? (
                                    <div style={{ fontSize: "12px", color: "#666", padding: "8px" }}>
                                        Có {allErrors.length} lỗi nhưng không có dòng nào được hiển thị.
                                    </div>
                                ) : null}
                            </div>
                        )}
                    </div>
                        );
                    })()}
                </>
            )}

            {/* Preview Table */}
            <div style={{ marginBottom: "20px" }}>
                <div style={{ fontSize: "14px", fontWeight: 500, marginBottom: "8px" }}>
                    Xem trước dữ liệu:
                </div>
                {validatedRows.length === 0 ? (
                    <div style={{ 
                        padding: "40px", 
                        textAlign: "center", 
                        color: "#666",
                        backgroundColor: "#f5f5f5",
                        borderRadius: "4px",
                        border: "1px solid #e0e0e0"
                    }}>
                        Chưa có dữ liệu được xác thực. Vui lòng chọn file và nhấn "Tiếp theo" để xác thực.
                    </div>
                ) : (
                    <div style={{ 
                        overflowX: "auto", 
                        border: "1px solid #e0e0e0", 
                        borderRadius: "4px", 
                        maxHeight: "calc(100vh - 400px)",
                        overflowY: "auto",
                        position: "relative"
                    }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px" }}>
                            <thead style={{ position: "sticky", top: 0, backgroundColor: "#f5f5f5", zIndex: 1 }}>
                                <tr>
                                    <th style={{ padding: "6px", textAlign: "left", border: "1px solid #e0e0e0", whiteSpace: "nowrap" }}>
                                        Dòng
                                    </th>
                                    {previewColumns.map((col) => (
                                        <th
                                            key={col.key}
                                            style={{ padding: "6px", textAlign: "left", border: "1px solid #e0e0e0", whiteSpace: "nowrap" }}
                                        >
                                            {col.label}
                                        </th>
                                    ))}
                                    <th style={{ padding: "6px", textAlign: "left", border: "1px solid #e0e0e0", whiteSpace: "nowrap", width: "40px" }}>
                                        ✓
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    [...validatedRows].sort((a, b) => {
                                        // Sort invalid rows first
                                        if (!a.isValid && b.isValid) return -1;
                                        if (a.isValid && !b.isValid) return 1;
                                        // Then sort by row number numerically
                                        return a.rowNumber - b.rowNumber;
                                    }).map((row, index) => {
                                        const sortedIndex = [...validatedRows].sort((a, b) => {
                                            if (!a.isValid && b.isValid) return -1;
                                            if (a.isValid && !b.isValid) return 1;
                                            return a.rowNumber - b.rowNumber;
                                        }).findIndex(r => r.rowNumber === row.rowNumber);
                                        
                                        return (
                                            <tr
                                                key={row.rowNumber}
                                                style={{
                                                    backgroundColor: row.isValid 
                                                        ? (sortedIndex % 2 === 0 ? "#fff" : "#f9f9f9")
                                                        : "#ffebee",
                                                }}
                                            >
                                                <td style={{ padding: "6px", border: "1px solid #e0e0e0" }}>
                                                    {row.rowNumber}
                                                    {!row.isValid && row.errors && row.errors.length > 0 && (
                                                        <div style={{ fontSize: "9px", color: "#c62828", marginTop: "2px" }}>
                                                            {row.errors.length} lỗi
                                                        </div>
                                                    )}
                                                </td>
                                                {previewColumns.map((col) => {
                                                    let cellValue: string | React.ReactNode = "-";
                                                    
                                                    if (col.isNested && col.key === "contact_list") {
                                                        // Handle contact_list nested data
                                                        const contactList = row.data?.[col.key];
                                                        if (contactList && Array.isArray(contactList) && contactList.length > 0) {
                                                            const contact = contactList[0]?.contact || {};
                                                            const contactParts = [];
                                                            if (contact.name) contactParts.push(`Tên: ${contact.name}`);
                                                            if (contact.phone) contactParts.push(`SĐT: ${contact.phone}`);
                                                            if (contact.email) contactParts.push(`Email: ${contact.email}`);
                                                            if (contact.position) contactParts.push(`Vị trí: ${contact.position}`);
                                                            cellValue = contactParts.length > 0 ? contactParts.join(", ") : "-";
                                                        }
                                                    } else {
                                                        const value = row.data?.[col.key];
                                                        if (value !== null && value !== undefined && value !== "") {
                                                            if (typeof value === "boolean") {
                                                                cellValue = value ? "Có" : "Không";
                                                            } else {
                                                                cellValue = String(value);
                                                            }
                                                        }
                                                    }
                                                    
                                                    // Check if this field has an error
                                                    const fieldError = row.errors?.find(e => e.field === col.key);
                                                    
                                                    return (
                                                        <td 
                                                            key={col.key} 
                                                            style={{ 
                                                                padding: "6px", 
                                                                border: "1px solid #e0e0e0",
                                                                maxWidth: "200px",
                                                                wordWrap: "break-word",
                                                                backgroundColor: fieldError ? "#ffcdd2" : "transparent"
                                                            }}
                                                            title={
                                                                fieldError 
                                                                    ? `Lỗi: ${fieldError.error}` 
                                                                    : typeof cellValue === "string" ? cellValue : ""
                                                            }
                                                        >
                                                            {cellValue}
                                                            {fieldError && (
                                                                <div style={{ fontSize: "9px", color: "#c62828", marginTop: "2px" }}>
                                                                    ⚠ {fieldError.error}
                                                                </div>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                                <td style={{ padding: "6px", border: "1px solid #e0e0e0", textAlign: "center" }}>
                                                    {row.isValid ? (
                                                        <span style={{ color: "#2e7d32", fontSize: "16px", fontWeight: "bold" }}>✓</span>
                                                    ) : (
                                                        <span style={{ color: "#c62828", fontSize: "16px", fontWeight: "bold" }}>✗</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                }
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "16px", marginTop: "24px" }}>
                <CustomButton
                    title="Quay lại"
                    onClick={onBack}
                    variant="outlined"
                    style={{
                        paddingTop: 8,
                        paddingBottom: 8,
                        paddingLeft: 14,
                        paddingRight: 14,
                        display: "flex",
                        justifyContent: "center",
                        minWidth: "120px",
                        height: "36px",
                        borderRadius: "5px",
                        fontSize: "14px",
                        gap: 6,
                    }}
                />
                <LoadingButton
                    variant="contained"
                    onClick={onNext}
                    disabled={invalidRows.length > 0 || isUploading}
                    isLoading={false}
                    text="Tiếp theo"
                    style={{
                        paddingTop: 8,
                        paddingBottom: 8,
                        paddingLeft: 14,
                        paddingRight: 14,
                        display: "flex",
                        justifyContent: "center",
                        minWidth: "120px",
                        height: "36px",
                        borderRadius: "5px",
                        fontSize: "14px",
                        gap: 6,
                    }}
                />
            </div>
        </>
        );
    };

    // Step 3: Confirmation
    const Step3Confirmation = () => (
        <>
            <h3 style={{ marginTop: 0, marginBottom: "20px", fontSize: "18px", fontWeight: 500 }}>
                Bước 3: Xác nhận và nhập dữ liệu
            </h3>

            {/* Summary Stats */}
            <div
                style={{
                    display: "flex",
                    gap: "12px",
                    marginBottom: "20px",
                    width: "100%",
                }}
            >
                {displaySummaryStats.map((stat, index) => (
                    <div
                        key={index}
                        style={{
                            flex: 1,
                            padding: "8px 12px",
                            backgroundColor: stat.backgroundColor || "#e8f5e9",
                            borderRadius: "4px",
                            border: `1px solid ${stat.borderColor || "#4caf50"}`,
                        }}
                    >
                        <div style={{ fontSize: "18px", fontWeight: "bold", color: stat.textColor || "#2e7d32" }}>
                            {stat.value}
                        </div>
                        <div style={{ fontSize: "11px", color: stat.textColor || "#2e7d32" }}>{stat.label}</div>
                    </div>
                ))}
            </div>

            <div
                style={{
                    padding: "16px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "4px",
                    marginBottom: "20px",
                }}
            >
                <div style={{ fontSize: "14px", fontWeight: 500, marginBottom: "12px" }}>Thông tin nhập:</div>
                <div style={{ fontSize: "12px", lineHeight: "1.8", color: "#666" }}>
                    <div>
                        <strong>File:</strong> {selectedFile?.name}
                    </div>
                    <div>
                        <strong>Chế độ nhập:</strong>{" "}
                        {importMode === "flush" ? (
                            <span style={{ color: "#d32f2f" }}>{importModeLabels.flush.title}</span>
                        ) : (
                            <span style={{ color: "#2e7d32" }}>{importModeLabels.update.title}</span>
                        )}
                    </div>
                </div>
            </div>

            {importMode === "flush" && (() => {
                // Get total rows processed from summary stats (last stat is usually "Tổng số dòng")
                const totalRowsStat = displaySummaryStats.find(stat => stat.label === "Tổng số dòng") || displaySummaryStats[displaySummaryStats.length - 1];
                const totalRowsToImport = totalRowsStat?.value || validatedRows.length;
                
                return (
                    <div
                        style={{
                            padding: "12px",
                            backgroundColor: "#ffebee",
                            borderRadius: "4px",
                            border: "1px solid #f44336",
                            marginBottom: "20px",
                        }}
                    >
                        <div style={{ fontWeight: 500, color: "#c62828", marginBottom: "4px" }}>⚠️ CẢNH BÁO</div>
                        <div style={{ fontSize: "12px", color: "#c62828" }}>
                            Bạn đã chọn chế độ "{importModeLabels.flush.title}". Tất cả dữ liệu hiện có sẽ bị xóa vĩnh viễn
                            và thay thế bằng {totalRowsToImport} bản ghi từ file. Hành động này không thể hoàn tác.
                        </div>
                    </div>
                );
            })()}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "16px", marginTop: "24px" }}>
                <CustomButton
                    title="Quay lại"
                    onClick={onBack}
                    disabled={isUploading}
                    variant="outlined"
                    style={{
                        paddingTop: 8,
                        paddingBottom: 8,
                        paddingLeft: 14,
                        paddingRight: 14,
                        display: "flex",
                        justifyContent: "center",
                        minWidth: "120px",
                        height: "36px",
                        borderRadius: "5px",
                        fontSize: "14px",
                        gap: 6,
                    }}
                />
                <LoadingButton
                    variant="contained"
                    onClick={onImport}
                    disabled={isUploading}
                    isLoading={isUploading}
                    text="Xác nhận và nhập dữ liệu"
                    style={{
                        paddingTop: 8,
                        paddingBottom: 8,
                        paddingLeft: 14,
                        paddingRight: 14,
                        display: "flex",
                        justifyContent: "center",
                        minWidth: "120px",
                        height: "36px",
                        borderRadius: "5px",
                        fontSize: "14px",
                        gap: 6,
                    }}
                />
            </div>
        </>
    );

    return (
        <>
            <StepIndicator />
            {currentStep === "file" && <Step1FileSelection />}
            {currentStep === "validation" && <Step2Validation />}
            {currentStep === "confirmation" && <Step3Confirmation />}
        </>
    );
};

